import React     from 'react'
import {Box}     from '@mui/material'
import {DASH_COUNT} from '../../util/readiness'

/*
 * The readiness meter: a green dot, five dashes, and a star at 90% (171).
 *
 * THE DOT IS STATIC AND ALWAYS GREEN, and it belongs to the PREPKIT meter only (172). It says "you
 * have started" — they have a PrepKit, tried or bought — and it does a second job: it puts the
 * finished colour on screen from day one, so five grey dashes read as "these will be green" rather
 * than as five things that are broken.
 *
 * A product's meter has no dot. "You have started" is a fact about the account, not about each
 * product, and repeating it down the page would make it look like a per-product state that can
 * differ — which it cannot.
 *
 * Three dash states and no more. Grey is untouched, green is banked, lime is under way. There is
 * deliberately NO red or amber here: score colour is the topic rows' job, and a readiness meter that
 * went red would be grading the student twice for the same thing in the same row of the screen.
 *
 * The in-progress dash does NOT graduate from lime toward green as it fills. A dash is ~22px, so a
 * hue swept across 20% of progress is unreadable — and the closer it got to green the harder it
 * would be to tell from a dash already earned, which is the one reading that must stay unambiguous.
 * If intra-dash precision is wanted, width is the honest encoding, not hue.
 */

const DASH_HEIGHT = 6
const IN_PROGRESS_LIME = `#c9eb1f`

const ReadinessMeter = ({readiness, custom, label = 'Exam Readiness', showLabel = true, showDot = true, dashWidth = 22}) => {
	if (!readiness) return null

	const {full, partial, star, percent} = readiness

	return (
		<Box
			// one string for assistive tech: the dashes are decorative once this is said out loud
			aria-label={`Exam Readiness ${percent}%${star ? ', top marks' : ''}`}
			sx={{display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0}}
		>
			{showLabel && (
				<Box component="span" sx={{
					fontSize: custom.fontSize.micro,
					fontWeight: custom.fontWeight.bold,
					textTransform: 'uppercase',
					letterSpacing: '0.08em',
					color: custom.text.muted,
					whiteSpace: 'nowrap',
					visibility: {xs: 'hidden', md: 'visible'}
				}}>
					{label}
				</Box>
			)}

			<Box aria-hidden sx={{display: 'flex', alignItems: 'center', gap: 0.625, flexShrink: 0}}>
				{/* the anchor, prepkit level only; see the note above */}
				{showDot && (
					<Box sx={{
						width: DASH_HEIGHT + 2,
						height: DASH_HEIGHT + 2,
						borderRadius: '50%',
						background: custom.status.greenStrong,
						flexShrink: 0,
					}}/>
				)}

				{Array.from({length: DASH_COUNT}).map((_, i) => (
					<Box
						key={i}
						sx={{
							width: dashWidth,
							height: DASH_HEIGHT,
							borderRadius: 999,
							flexShrink: 0,
							background: i < full
								? custom.status.green
								: (partial && i === full ? IN_PROGRESS_LIME : custom.progressEmpty),
						}}
					/>
				))}

				{/*
				  * The star is additive, never a substitute for a dash — it sits outside the five so
				  * it cannot be misread as a sixth.
				  */}
				{star && (
					<Box component="span" sx={{ml: 0.25, fontSize: 14, lineHeight: 1}}>⭐</Box>
				)}
			</Box>
		</Box>
	)
}

export default ReadinessMeter
